import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams } from 'react-router-dom';

import { backendURL } from '../common/api';

type ProgramType = 'general' | 'phungsan';
type Answer = string;

type FormValues = {
  name: string;
  gender: string;
  mobilePhone: string;
  company: string;
  position: string;
  souvenirDeliveryAddress: string;
  lessonClinic: Answer;
  airportPickup: Answer;
  golfBagDelivery: Answer;
  golfBagDeliveryAddress: string;
  golfBagPickupDate: string;
  golfBagDeliveryContact: string;
  accommodation: Answer;
  returnShuttle: Answer;
  specialMeal: string;
  personalInfoAgreed: boolean;
  thirdPartyAgreed: boolean;
};

const emptyForm: FormValues = {
  name: '',
  gender: '',
  mobilePhone: '',
  company: '',
  position: '',
  souvenirDeliveryAddress: '',
  lessonClinic: '',
  airportPickup: '',
  golfBagDelivery: '',
  golfBagDeliveryAddress: '',
  golfBagPickupDate: '',
  golfBagDeliveryContact: '',
  accommodation: '',
  returnShuttle: '',
  specialMeal: '',
  personalInfoAgreed: false,
  thirdPartyAgreed: false,
};

const isProgramType = (value: string): value is ProgramType =>
  value === 'general' || value === 'phungsan';

const sectionTitleSx = {
  backgroundColor: '#292929',
  color: '#d9bf75',
  fontWeight: 700,
  px: 2,
  py: 1.5,
};

const labelSx = {
  color: '#333333',
  fontWeight: 700,
  mb: 1,
};

export default function KpgaInvitation() {
  const { programType: programTypeParam } = useParams<{ programType: string }>();
  const programType =
    typeof programTypeParam === 'string' && isProgramType(programTypeParam)
      ? programTypeParam
      : undefined;
  const [form, setForm] = React.useState<FormValues>(emptyForm);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(
    programType ? '' : '신청 링크가 유효하지 않습니다. 사무국에 문의해 주세요.',
  );
  const [success, setSuccess] = React.useState(false);

  function update<K extends keyof FormValues>(field: K, value: FormValues[K]) {
    setForm(current => ({ ...current, [field]: value }));
    setSuccess(false);
  }

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!programType) {
      setError('신청 링크가 유효하지 않습니다. 사무국에 문의해 주세요.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess(false);

    const payload = {
      name: form.name.trim(),
      gender: form.gender,
      mobile_phone: form.mobilePhone.trim(),
      company: form.company.trim(),
      position: form.position.trim(),
      souvenir_delivery_address: form.souvenirDeliveryAddress.trim(),
      lesson_clinic: form.lessonClinic === 'yes',
      airport_pickup: programType === 'phungsan' ? form.airportPickup === 'yes' : null,
      golf_bag_delivery: programType === 'phungsan' ? form.golfBagDelivery === 'yes' : null,
      golf_bag_delivery_address:
        programType === 'phungsan' && form.golfBagDelivery === 'yes'
          ? form.golfBagDeliveryAddress.trim()
          : '',
      golf_bag_pickup_date:
        programType === 'phungsan' && form.golfBagDelivery === 'yes'
          ? form.golfBagPickupDate
          : null,
      golf_bag_delivery_contact:
        programType === 'phungsan' && form.golfBagDelivery === 'yes'
          ? form.golfBagDeliveryContact.trim()
          : '',
      accommodation: programType === 'phungsan' ? form.accommodation === 'yes' : null,
      return_shuttle: programType === 'phungsan' ? form.returnShuttle === 'yes' : null,
      special_meal: form.specialMeal.trim(),
      personal_info_agreed: form.personalInfoAgreed,
      third_party_agreed: form.thirdPartyAgreed,
    };

    fetch(`${backendURL}kpga-api/participants/${programType}`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'content-type': 'application/json' },
    })
      .then(async response => {
        const responseBody = await response.json().catch(() => ({}));
        if (!response.ok) {
          const firstError = Object.values(responseBody).flat()[0];
          throw new Error(
            typeof firstError === 'string'
              ? firstError
              : '정보를 저장하지 못했습니다. 입력 내용을 확인해 주세요.',
          );
        }
        setSuccess(true);
      })
      .catch(requestError => {
        setError(requestError.message || '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      })
      .finally(() => setSubmitting(false));
  };

  const phungsan = programType === 'phungsan';

  return (
    <Box className="kpga-page">
      <Box component="header" sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          component="img"
          src="/kpga-tour-championship-logo.png"
          alt="KPGA TOUR Championship in Jeju"
          sx={{ display: 'block', width: '100%', maxWidth: 356, height: 'auto', mx: 'auto' }}
        />
        <Typography sx={{ color: '#d9bf75', fontWeight: 700, mt: 3 }}>
          프로암 신청서
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {success ? (
        <Card className="kpga-card" role="status" sx={{ mt: 4, textAlign: 'center' }}>
          <CardContent sx={{ px: { xs: 3, sm: 6 }, py: { xs: 5, sm: 7 } }}>
            <CheckCircleOutlineIcon sx={{ color: '#32805b', fontSize: 76, mb: 2 }} />
            <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
              제출이 완료되었습니다
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              프로암 참가 정보가 정상적으로 접수되었습니다. 감사합니다.
            </Typography>
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8f7f3' }}>
              <Typography>2026년 11월 3일(화) 11:30 샷건</Typography>
              <Typography>테디밸리 골프앤리조트</Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 3 }}>
              문의: KPGA TOUR CHAMPIONSHIP in Jeju 프로암 운영 사무국
            </Typography>
            <Typography variant="body2">
              전화: <a href="tel:010-4768-8061">010-4768-8061</a>
            </Typography>
            <Typography variant="body2">
              이메일: <a href="mailto:whossol@brion.co.kr">whossol@brion.co.kr</a>
            </Typography>
          </CardContent>
        </Card>
      ) : !error && (
        <form onSubmit={submit}>
          <Card className="kpga-card">
            <Typography sx={sectionTitleSx}>대회 정보</Typography>
            <CardContent>
              <Stack spacing={1}>
                <Typography><strong>일시</strong>　2026년 11월 3일(화) 11:30 샷건</Typography>
                <Typography><strong>장소</strong>　테디밸리 골프앤리조트</Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card className="kpga-card" sx={{ mt: 2 }}>
            <Typography sx={sectionTitleSx}>고객님의 정보를 확인 및 입력해 주세요</Typography>
            <CardContent>
              <Stack spacing={2.5}>
                <TextField
                  label="성함"
                  value={form.name}
                  onChange={event => update('name', event.target.value)}
                  required
                  fullWidth
                />
                <FormControl required>
                  <FormLabel sx={labelSx}>성별</FormLabel>
                  <RadioGroup row value={form.gender} onChange={event => update('gender', event.target.value)}>
                    <FormControlLabel value="M" control={<Radio required />} label="남" />
                    <FormControlLabel value="F" control={<Radio required />} label="여" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  label="본인 핸드폰번호"
                  type="tel"
                  value={form.mobilePhone}
                  onChange={event => update('mobilePhone', event.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="회사"
                  value={form.company}
                  onChange={event => update('company', event.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="직위"
                  value={form.position}
                  onChange={event => update('position', event.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="프로암 기념품 수령 주소"
                  value={form.souvenirDeliveryAddress}
                  onChange={event => update('souvenirDeliveryAddress', event.target.value)}
                  required
                  multiline
                  minRows={2}
                  fullWidth
                />
                <FormControl required>
                  <FormLabel sx={labelSx}>레슨 클리닉 진행 여부</FormLabel>
                  <RadioGroup aria-label="레슨 클리닉 진행 여부" row value={form.lessonClinic} onChange={event => update('lessonClinic', event.target.value)}>
                    <FormControlLabel value="yes" control={<Radio required />} label="진행" />
                    <FormControlLabel value="no" control={<Radio required />} label="미진행" />
                  </RadioGroup>
                </FormControl>

                {phungsan && (
                  <>
                    <FormControl required>
                      <FormLabel sx={labelSx}>제주공항 → 테디밸리 골프앤리조트 픽업 서비스 (11/2)</FormLabel>
                      <RadioGroup aria-label="제주공항 픽업 서비스 (11/2)" row value={form.airportPickup} onChange={event => update('airportPickup', event.target.value)}>
                        <FormControlLabel value="yes" control={<Radio required />} label="진행" />
                        <FormControlLabel value="no" control={<Radio required />} label="미진행" />
                      </RadioGroup>
                    </FormControl>
                    <FormControl required>
                      <FormLabel sx={labelSx}>숙박 여부 (11/2)</FormLabel>
                      <RadioGroup
                        aria-label="숙박 여부 (11/2)"
                        row
                        value={form.accommodation}
                        onChange={event => update('accommodation', event.target.value)}
                      >
                        <FormControlLabel value="yes" control={<Radio required />} label="예" />
                        <FormControlLabel value="no" control={<Radio required />} label="아니요" />
                      </RadioGroup>
                    </FormControl>
                    <FormControl required>
                      <FormLabel sx={labelSx}>테디밸리 → 제주공항 셔틀 이용 여부 (11/3)</FormLabel>
                      <RadioGroup
                        aria-label="테디밸리 → 제주공항 셔틀 이용 여부 (11/3)"
                        row
                        value={form.returnShuttle}
                        onChange={event => update('returnShuttle', event.target.value)}
                      >
                        <FormControlLabel value="yes" control={<Radio required />} label="이용" />
                        <FormControlLabel value="no" control={<Radio required />} label="미이용" />
                      </RadioGroup>
                    </FormControl>
                    <FormControl required>
                      <FormLabel sx={labelSx}>골프백 픽업 DTD 서비스</FormLabel>
                      <RadioGroup aria-label="골프백 픽업 DTD 서비스" row value={form.golfBagDelivery} onChange={event => update('golfBagDelivery', event.target.value)}>
                        <FormControlLabel value="yes" control={<Radio required />} label="진행" />
                        <FormControlLabel value="no" control={<Radio required />} label="미진행" />
                      </RadioGroup>
                    </FormControl>
                    {form.golfBagDelivery === 'yes' && (
                      <Box className="kpga-delivery-fields">
                        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                          골프백 픽업은 10월 28일(수) 또는 10월 29일(목) 중 희망하시는 날짜를 선택해 주세요.
                          대회 종료 후 골프백은 11월 6일(금)부터 11월 7일(토) 중 다시 받아보실 수 있습니다.
                          <br />* 업체 사정에 따라 일정 변동 가능성 있음
                        </Typography>
                        <Stack spacing={2}>
                          <TextField
                            label="골프백 배송 주소"
                            value={form.golfBagDeliveryAddress}
                            onChange={event => update('golfBagDeliveryAddress', event.target.value)}
                            required
                            fullWidth
                          />
                          <TextField
                            select
                            label="골프백 픽업 희망 날짜"
                            value={form.golfBagPickupDate}
                            onChange={event => update('golfBagPickupDate', event.target.value)}
                            required
                            fullWidth
                          >
                            <MenuItem value="2026-10-28">10/28(수)</MenuItem>
                            <MenuItem value="2026-10-29">10/29(목)</MenuItem>
                          </TextField>
                          <TextField
                            label="골프백 서비스 연락처"
                            helperText="본인 연락처와 다를 경우 입력해 주세요."
                            type="tel"
                            value={form.golfBagDeliveryContact}
                            onChange={event => update('golfBagDeliveryContact', event.target.value)}
                            fullWidth
                          />
                        </Stack>
                      </Box>
                    )}
                  </>
                )}

                <TextField
                  label="채식/알레르기 등 식사 관련 요청"
                  value={form.specialMeal}
                  onChange={event => update('specialMeal', event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Stack>
            </CardContent>
          </Card>

          <Card className="kpga-card" sx={{ mt: 2 }}>
            <Typography sx={sectionTitleSx}>개인정보 동의</Typography>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                KPGA TOUR CHAMPIONSHIP in Jeju 프로암 RSVP - 개인정보 수집 동의
              </Typography>
              <Typography variant="body2" className="kpga-privacy-copy">
                {`대회 사무국은 참가 신청서 작성 및 대회 운영을 위해 고객님의 개인정보를 수집하고자 합니다.

수집된 개인정보는 프로암 참가 고객 관리 및 대회 운영 목적을 위해 사용됩니다. 동의하신 개인정보는 위에 명시된 수집 및 이용 목적 외로 사용되지 않습니다. 수집된 개인정보는 대회 종료 후 안전하게 파기됩니다.

[개인정보 수집 이용 동의]
- 개인정보 수집 항목: 이름, 성별, 연락처, 회사, 직위, 프로암 기념품 수령 주소, 대회 관련 서비스 이용 여부 및 식사 요청
- 개인정보 수집 및 이용목적: 참가 고객 관리 및 대회 운영
- 보유 및 이용기간: 행사 종료 후 1개월간 보관 후 파기

귀하는 동의를 거부할 권리가 있으며, 동의 거부 시 대회 참가 신청이 제한될 수 있음을 안내드립니다. 개인정보 수집 및 이용에 동의하십니까?`}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    required
                    checked={form.personalInfoAgreed}
                    onChange={event => update('personalInfoAgreed', event.target.checked)}
                  />
                }
                label="개인정보 수집 및 이용에 동의합니다."
                sx={{ mt: 1 }}
              />

              <Typography variant="body2" className="kpga-privacy-copy" sx={{ mt: 2 }}>
                {`[개인정보 제3자 제공 동의]
- 제공받는 자: 브리온컴퍼니
- 제공받는 자의 이용 목적: 참가 고객 관리 및 대회 운영
                - 제공받는 항목: 이름, 성별, 연락처, 회사, 직위, 프로암 기념품 수령 주소, 대회 관련 서비스 이용 여부 및 식사 요청
- 제공받는 자의 보유 및 이용기간: 행사 종료 후 1개월간 보관 후 파기

귀하는 동의를 거부할 권리가 있으며, 동의 거부 시 대회 참가 신청이 제한될 수 있음을 안내드립니다. 개인정보 제3자 제공에 동의하십니까?`}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    required
                    checked={form.thirdPartyAgreed}
                    onChange={event => update('thirdPartyAgreed', event.target.checked)}
                  />
                }
                label="개인정보 제3자 제공에 동의합니다."
                sx={{ mt: 1 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={700}>
                  문의: KPGA TOUR CHAMPIONSHIP in Jeju 프로암 운영 사무국
                </Typography>
                <Typography variant="body2">
                  전화: <a href="tel:010-4768-8061">010-4768-8061</a>
                </Typography>
                <Typography variant="body2">
                  이메일: <a href="mailto:whossol@brion.co.kr">whossol@brion.co.kr</a>
                </Typography>
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                fullWidth
                sx={{
                  mt: 3,
                  py: 1.5,
                  backgroundColor: '#292929',
                  color: '#d9bf75',
                  fontWeight: 700,
                  '&:hover': { backgroundColor: '#111111' },
                }}
              >
                {submitting ? '제출 중...' : '제출'}
              </Button>
            </CardContent>
          </Card>
        </form>
      )}
    </Box>
  );
}
